import type { FC, ReactNode } from 'react'
import type { NavCollection } from '@/types'

import Link from 'next/link'
import { useEffect, useRef } from 'react'
import { useRouter } from 'next/router'
import { X } from 'lucide-react'
import { cn } from '@/lib/utils'
import { cms } from '@/lib/cms'
import { Wordmark } from '@/components/generals/wordmark'
import { clientServices, maisonLinks } from '@/constants/site'

interface MenuDrawerProps {
    open: boolean
    onClose: () => void
    navigation: Array<NavCollection>
}

const SERVICE_LINKS = [
    { label: 'Book an appointment', href: '/appointment' },
    { label: 'Your bag', href: '/bag' },
    { label: 'Privacy policy', href: '/privacy' }
]

export const MenuDrawer: FC<MenuDrawerProps> = ({ open, onClose, navigation }): ReactNode => {
    const router = useRouter()
    const panelRef = useRef<HTMLDivElement>(null)
    const closeRef = useRef<HTMLButtonElement>(null)

    useEffect(() => {
        router.events.on('routeChangeStart', onClose)

        return () => router.events.off('routeChangeStart', onClose)
    }, [router.events, onClose])

    useEffect(() => {
        if (!open) return

        const previous = document.activeElement instanceof HTMLElement ? document.activeElement : null
        const overflow = document.body.style.overflow

        document.body.style.overflow = 'hidden'
        closeRef.current?.focus()

        const onKeyDown = (event: KeyboardEvent): void => {
            if (event.key === 'Escape') {
                onClose()

                return
            }

            if (event.key !== 'Tab' || !panelRef.current) return

            const focusable = panelRef.current.querySelectorAll<HTMLElement>('a[href], button:not([disabled])')

            if (focusable.length === 0) return

            const first = focusable[0]
            const last = focusable[focusable.length - 1]

            if (event.shiftKey && document.activeElement === first) {
                event.preventDefault()
                last.focus()
            }

            else if (!event.shiftKey && document.activeElement === last) {
                event.preventDefault()
                first.focus()
            }
        }

        document.addEventListener('keydown', onKeyDown)

        return () => {
            document.body.style.overflow = overflow
            document.removeEventListener('keydown', onKeyDown)
            previous?.focus()
        }
    }, [open, onClose])

    return (
        <div
            inert={!open}
            className={cn('fixed inset-0 z-50 transition-[visibility] duration-500', open ? 'visible' : 'invisible')}
        >
            <div
                aria-hidden
                onClick={onClose}
                className={cn('absolute inset-0 bg-foreground/35 transition-opacity duration-500', open ? 'opacity-100' : 'opacity-0')}
            />

            <div
                ref={panelRef}
                id='site-menu'
                role='dialog'
                aria-modal='true'
                aria-label='Menu'
                className={cn(
                    'absolute inset-y-0 left-0 flex w-full max-w-[540px] flex-col overflow-y-auto bg-background transition-transform duration-500 ease-[cubic-bezier(0.22,1,0.36,1)]',
                    open ? 'translate-x-0' : '-translate-x-full'
                )}
            >
                <div className='flex h-16 shrink-0 items-center justify-between px-5 sm:px-8 lg:h-[72px] lg:px-12'>
                    <button
                        ref={closeRef}
                        type='button'
                        onClick={onClose}
                        className='-ml-2 flex items-center gap-3 p-2'
                    >
                        <X className='size-5' strokeWidth={1.25} aria-hidden />
                        <span className='eyebrow'>
                            Close
                        </span>
                    </button>

                    <Wordmark className='text-lg' />
                </div>

                <nav aria-label='Main' className='flex flex-1 flex-col px-5 pb-10 pt-8 sm:px-8 lg:px-12'>
                    <p className='eyebrow text-muted-foreground'>
                        Collections
                    </p>

                    <ul className='mt-5 flex flex-col'>
                        {navigation.map((collection, index) => (
                            <li key={collection.id} className='border-b border-border'>
                                <Link
                                    href={`/collections/${collection.slug}`}
                                    className='group flex items-baseline gap-5 py-4'
                                >
                                    <span className='w-6 text-[0.6875rem] tabular-nums text-muted-foreground'>
                                        {String(index + 1).padStart(2, '0')}
                                    </span>

                                    <span
                                        data-mdlfy-cms={cms('collections', collection.id, 'name')}
                                        className='font-heading text-[2rem] font-light leading-none transition-transform duration-500 group-hover:translate-x-2 sm:text-[2.5rem]'
                                    >
                                        {collection.name}
                                    </span>
                                </Link>
                            </li>
                        ))}
                    </ul>

                    <ul className='mt-10 grid grid-cols-2 gap-x-6 gap-y-4'>
                        {[...maisonLinks, ...SERVICE_LINKS].map(link => (
                            <li key={link.href}>
                                <Link href={link.href} className='eyebrow link-grow'>
                                    {link.label}
                                </Link>
                            </li>
                        ))}
                    </ul>

                    <div className='mt-auto pt-12 text-sm leading-relaxed text-muted-foreground'>
                        <p className='eyebrow text-foreground'>
                            Client services
                        </p>

                        <p className='mt-3'>
                            <a href={`tel:${clientServices.phone.replace(/[^\d+]/g, '')}`} className='link-underline text-foreground'>
                                {clientServices.phone}
                            </a>
                        </p>

                        <p className='mt-2'>
                            {clientServices.hours}
                        </p>
                    </div>
                </nav>
            </div>
        </div>
    )
}
